"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PhoneCall, Search, ShieldCheck, Clock, CircleCheck as CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const steps = [{
  step: "01",
  title: "Tell Us What's Happening",
  description: "Call us or submit a service request to get started with your plumbing solution",
  icon: PhoneCall
}, {
  step: "02",
  title: "Describe the Symptoms",
  description: "Provide your location and describe what you're experiencing with your plumbing",
  icon: Search
}, {
  step: "03",
  title: "Get the Appropriate Service",
  description: "We evaluate your issue and recommend the right plumbing service to fix it",
  icon: ShieldCheck
}];
export default function Diagnosis() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  return <section id="how-we-help" data-nav="light" className="relative bg-background py-16 md:py-24 border-b border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        
        {/* Header Eyebrow & Main Title */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 rounded-full bg-secondary px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-secondary-foreground mb-4">
            <Clock className="size-3.5 text-accent" />
            <span>Problem-Focused Service System</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default leading-tight">
            How We Help
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Plumbing disasters don&apos;t wait, and guessing causes expensive property destruction. Our licensed Jonesboro plumbers arrive equipped with digital inspection tools to identify the exact point of failure within minutes.
          </p>
        </div>

        {/* 2-Column Split: Steps & Media Column */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          
          {/* Diagnostic Steps Column */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.1
        }} className="lg:col-span-7 space-y-4">
            {steps.map((step, index) => {
            const Icon = step.icon;
            return <motion.div key={step.step} data-index={index} variants={itemVariants}>
                  <Card className="border border-border bg-card shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md py-4 sm:py-5">
                    <CardContent className="px-4 sm:px-6 flex items-start gap-4 sm:gap-5">
                      <div className="flex flex-col items-center shrink-0">
                        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-primary font-bold">
                          <Icon className="size-6 text-primary" />
                        </div>
                        <span className="mt-2 text-xs font-bold text-muted-foreground font-mono">
                          {step.step}
                        </span>
                      </div>

                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg sm:text-xl font-bold text-foreground font-default">
                          {step.title}
                        </h3>
                        <p className="mt-1.5 text-sm sm:text-base text-muted-foreground leading-relaxed">
                          {step.description}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </motion.div>

          {/* Supporting Visual & Rapid Response Card */}
          <div className="lg:col-span-5 space-y-6">
            
            {/* Image Container with Verification Badge */}
            <div className="relative rounded-2xl overflow-hidden border border-border bg-muted shadow-md">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/plumber_diagnostic_pipe_inspection.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/plumber_diagnostic_pipe_inspection.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/plumber_diagnostic_pipe_inspection.png-wvc-sizes" alt="Licensed emergency plumber conducting video pipe inspection in Jonesboro" loading="lazy" decoding="async" className="w-full h-72 sm:h-84 lg:h-96 object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-transparent to-transparent pointer-events-none" />
              
              {/* Floating Overlay Badge */}
              <div className="absolute bottom-4 left-4 right-4 bg-background/95 backdrop-blur-xs rounded-xl p-3.5 border border-border flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground font-bold">
                  <CheckCircle2 className="size-6 text-accent-foreground" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Local Verification</p>
                  <p className="text-sm font-bold text-foreground">Accurate Diagnosis Guaranteed</p>
                </div>
              </div>
            </div>

            {/* Direct Dual Action Conversion Cluster */}
            <Card className="border-l-4 border-l-accent border-border bg-card shadow-sm">
              <CardContent className="p-6">
                <h4 className="text-lg font-bold text-foreground font-default">
                  Facing an active leak or backed up drain?
                </h4>
                <p className="mt-2 text-sm text-muted-foreground">
                  Our dispatch vans in Jonesboro are stocked with industrial leak detection and camera line tools for 24/7 emergency response.
                </p>

                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <Button asChild size="lg" className="w-full sm:w-auto bg-accent text-accent-foreground hover:bg-accent/90 rounded-full font-bold shadow-sm transition-[transform,box-shadow] duration-150 active:scale-98">
                    <a href="tel:4708479076" className="inline-flex items-center justify-center gap-2">
                      <PhoneCall className="size-4" />
                      <span>Call (470) 847-9076</span>
                    </a>
                  </Button>

                  <Button asChild variant="outline" size="lg" className="w-full sm:w-auto rounded-full font-semibold border-border hover:bg-secondary text-foreground">
                    <Link to="/contact/" className="inline-flex items-center justify-center gap-2">
                      <span>Request Service</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

          </div>

        </div>

      </div>
    </section>;
}